import AppLoadingButton from "@/components/mui/LoadingButton";
import Modal from "@/components/mui/Modal";
import {
  APP_STORE_ANDROID_URL,
  APP_STORE_IOS_URL,
} from "@/constant/Constant";
import { getUAInfo, getUrlParams } from "@/utils/utils";
import { Stack, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";

const DEFAULT_DEADLINE = Date.UTC(2026, 8, 15, 0, 0, 0);

const getDeadline = () => {
  const deadlineParam = getUrlParams('deadline');
  if (deadlineParam) {
    const parsed = Number(deadlineParam);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return DEFAULT_DEADLINE;
};

const isAfterDeadline = () => Date.now() > getDeadline();

interface OpenOrDownloadAppProps {
  open: boolean;
  onClose: () => void;
}

const getStoreUrl = () => {
  const { os } = getUAInfo();
  const osName = os?.name?.toLowerCase();

  if (osName === 'android') {
    return APP_STORE_ANDROID_URL;
  }
  return APP_STORE_IOS_URL;
};

const buttonBaseSx = {
  height: 40,
  minHeight: 40,
  borderRadius: '24px',
  px: 2,
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1.5,
};

const OpenOrDownloadApp = ({ open, onClose }: OpenOrDownloadAppProps) => {
  const { t } = useTranslation();
  const afterDeadline = isAfterDeadline();

  const handleGoToRedotPay = () => {
    window.open(getStoreUrl(), '_blank');
  };

  const preventClose = (_event: object, reason?: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={preventClose}
      disableEscapeKeyDown
      width="calc(100% - 32px)"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.72)',
          },
        },
      }}
      paperSx={(theme) => {
        const isDark = theme.palette.mode === 'dark';
        return {
          width: "90vw",
          maxWidth: "400px",
          borderRadius: '12px',
          bgcolor: isDark ? 'rgba(247, 248, 250, 0.2)' : theme.palette.background.popup,
          backdropFilter: isDark ? 'blur(24px)' : undefined,
          boxShadow: isDark
            ? '0 12px 40px rgba(0, 0, 0, 0.65)'
            : '0 12px 40px rgba(16, 5, 28, 0.15)',
          border: `0.5px solid ${
            isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(16, 5, 28, 0.12)'
          }`,
        };
      }}
    >
      <Stack sx={{ px: 2, py: 2.5, gap: 3, alignItems: 'center' }}>
        <Typography
          sx={{
            width: '100%',
            fontSize: 16,
            fontWeight: 500,
            lineHeight: 1.5,
            color: 'text.primary',
            wordBreak: 'break-word',
          }}
        >
          {afterDeadline ? t('register_download_app_title_ended') : t('register_download_app_title')}
        </Typography>
        <Typography
          component="div"
          sx={(theme) => ({
            width: '100%',
            fontSize: 12,
            lineHeight: 1.6,
            color: theme.palette.mode === 'dark' ? '#BCBCBC' : theme.palette.text.secondary,
            whiteSpace: 'pre-line',
            wordBreak: 'break-word',
          })}
        >
          <Trans
            i18nKey="register_download_app_desc"
            components={{
              bold: (
                <Typography
                  component="strong"
                  sx={{
                    display: 'inline',
                    fontSize: 'inherit',
                    lineHeight: 'inherit',
                    fontWeight: 500,
                    color: 'text.primary',
                  }}
                />
              ),
            }}
          />
        </Typography>
        <Stack sx={{ width: '100%', px: 2, gap: 1.5, alignItems: 'center' }}>
          <AppLoadingButton
            fullWidth
            variant="contained"
            onClick={handleGoToRedotPay}
            sx={(theme) => ({
              ...buttonBaseSx,
              ...(theme.palette.mode === 'dark' && {
                backgroundColor: '#FFFFFF',
                color: '#191919',
                '&:hover': {
                  backgroundColor: '#FFFFFF',
                },
              }),
            })}
          >
            {t('go_to_redotpay')}
          </AppLoadingButton>
          {!afterDeadline && (
            <AppLoadingButton
              fullWidth
              variant="outlined"
              onClick={onClose}
              sx={(theme) => {
                const borderColor = theme.palette.mode === 'dark'
                  ? '#BCBCBC'
                  : 'rgba(16, 5, 28, 0.2)';
                return {
                  ...buttonBaseSx,
                  borderWidth: '1px',
                  borderColor,
                  color: 'text.primary',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    borderWidth: '1px',
                    borderColor,
                    backgroundColor: 'transparent',
                  },
                };
              }}
            >
              Close
            </AppLoadingButton>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
};

export default OpenOrDownloadApp;
